import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";
import { AuthModal } from "../components/AuthModal";
import Marquee from "react-fast-marquee";
import { motion } from "framer-motion";
import axios from "axios";
import { Calendar, CreditCard, Globe, LayoutGrid, Shield, ArrowRight, CheckCircle, Users, Crown, Sparkles, Briefcase, Search, Star, Zap, MapPin } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0, transition: { duration: 0.5 } } };
const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.12 } } };

export default function Landing() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [showAuth, setShowAuth] = useState(false);
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    fetchFeatured();
  }, []);

  const fetchFeatured = async () => {
    try {
      const res = await axios.get(`${API}/featured`);
      setFeatured(res.data || []);
    } catch {}
  };

  const goAction = (role) => {
    if (user) {
      if (user.role === role) navigate(role === "customer" ? "/customer" : "/business");
      else navigate("/role-select");
    } else setShowAuth(true);
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="landing-page">
      {/* Navbar */}
      <nav className="sticky top-0 z-50 bg-white border-b-2 border-[#09090B]" data-testid="landing-nav">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-6 py-4">
          <span className="font-heading text-2xl tracking-tight text-[#09090B]">
            Book<span className="text-[#FF4A00]">Easy</span>
          </span>
          <button
            onClick={() => user ? navigate("/role-select") : setShowAuth(true)}
            className="btn-brutal bg-[#FF4A00] text-white text-sm"
            data-testid="nav-login-btn"
          >
            {user ? "Dashboard" : "Accedi"}
          </button>
        </div>
      </nav>

      {/* HERO — Bold & Asymmetric */}
      <section className="relative overflow-hidden bg-[#FFE600] border-b-2 border-[#09090B]" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-6 py-16 md:py-24 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left - Text */}
          <motion.div initial={{ opacity: 0, x: -40 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }}>
            <div className="inline-block bg-[#09090B] text-[#FFE600] text-xs font-bold uppercase tracking-widest px-4 py-2 rounded-full mb-6">
              La piattaforma #1 in Italia
            </div>
            <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl tracking-tighter text-[#09090B] leading-[0.95] mb-6">
              PRENOTA<br />
              <span className="text-[#FF4A00]">QUALSIASI</span><br />
              COSA.
            </h1>
            <p className="text-lg md:text-xl text-[#3F3F46] max-w-lg mb-8 leading-relaxed">
              Medici, ristoranti, parrucchieri, meccanici, avvocati... Trova, prenota e paga in pochi secondi.
            </p>
            <div className="flex flex-wrap gap-4">
              <button
                onClick={() => goAction("customer")}
                className="btn-brutal bg-[#FF4A00] text-white text-base"
                data-testid="hero-cta-book"
              >
                Prenota Ora <ArrowRight className="inline ml-2 h-5 w-5" />
              </button>
              <button
                onClick={() => goAction("business")}
                className="btn-brutal bg-white text-[#09090B] text-base"
                data-testid="hero-cta-biz"
              >
                Registra Attivita
              </button>
            </div>
            {/* Stats Row */}
            <div className="flex gap-6 mt-10">
              {[
                { val: "500+", label: "Attivita", color: "bg-[#00E5FF]" },
                { val: "10k+", label: "Prenotazioni", color: "bg-[#FF007F]" },
                { val: "44", label: "Categorie", color: "bg-[#CCFF00]" },
              ].map((s) => (
                <div key={s.label} className={`${s.color} border-2 border-[#09090B] rounded-xl px-4 py-3 shadow-brutal-sm`}>
                  <span className="font-heading text-2xl text-[#09090B]">{s.val}</span>
                  <span className="block text-xs font-bold uppercase tracking-wider text-[#09090B]/70">{s.label}</span>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Right - Image Collage */}
          <motion.div
            initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, delay: 0.2 }}
            className="relative hidden lg:block"
          >
            <div className="relative">
              <div className="w-full h-[420px] bg-white border-4 border-[#09090B] rounded-3xl shadow-brutal-lg overflow-hidden">
                <img
                  src="https://images.pexels.com/photos/7518736/pexels-photo-7518736.jpeg"
                  alt="Business"
                  className="w-full h-full object-cover"
                />
              </div>
              {/* Floating sticker cards */}
              <div className="absolute -bottom-4 -left-6 bg-[#FF007F] text-white border-2 border-[#09090B] rounded-2xl px-5 py-3 shadow-brutal-sm rotate-[-3deg]">
                <Star className="inline h-5 w-5 mr-1" /> 4.9/5 Voto
              </div>
              <div className="absolute -top-4 -right-6 bg-[#00E5FF] text-[#09090B] border-2 border-[#09090B] rounded-2xl px-5 py-3 shadow-brutal-sm rotate-[3deg]">
                <Zap className="inline h-5 w-5 mr-1" /> Prenota in 30sec
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FEATURED CAROUSEL — Always visible */}
      <section className="py-10 px-6 bg-[#09090B] border-b-2 border-[#09090B]" data-testid="featured-section">
        <div className="max-w-7xl mx-auto mb-6 flex items-center gap-3">
          <Crown className="h-5 w-5 text-[#FFE600]" />
          <span className="text-sm font-bold uppercase tracking-widest text-[#FFE600]">In Evidenza</span>
          <span className="text-xs text-white/30 ml-auto">Sponsorizzato</span>
        </div>
        <Marquee speed={35} gradient={false} pauseOnHover>
          {featured.map((f) => (
            <div
              key={f.feature_id}
              className="mx-3 w-[260px] bg-white border-2 border-[#09090B] rounded-2xl overflow-hidden shadow-brutal-sm hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-all duration-200 cursor-pointer group"
              onClick={() => navigate(`/book/${f.business_id}`)}
              data-testid={`featured-${f.feature_id}`}
            >
              <div className="h-36 bg-[#FFE600] overflow-hidden">
                {f.image_url ? (
                  <img src={f.image_url} alt={f.business_name} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <span className="font-heading text-5xl text-[#09090B]/20">{f.business_name?.[0]}</span>
                  </div>
                )}
              </div>
              <div className="p-4">
                <div className="flex items-center gap-2 mb-1">
                  <Crown className="h-3.5 w-3.5 text-[#FF4A00]" />
                  <span className="text-[10px] font-bold uppercase tracking-widest text-[#FF4A00]">In Evidenza</span>
                </div>
                <h3 className="font-heading text-base text-[#09090B] group-hover:text-[#FF4A00] transition-colors">{f.business_name}</h3>
                <p className="text-[11px] text-[#3F3F46] mt-0.5">{t(`cat.${f.business_category}`)}</p>
              </div>
            </div>
          ))}
          {/* Empty placeholder slots */}
          {Array.from({ length: Math.max(6 - featured.length, 3) }).map((_, i) => (
            <div
              key={`empty-${i}`}
              className="mx-3 w-[260px] bg-white/5 border-2 border-dashed border-white/20 rounded-2xl overflow-hidden hover:border-[#FFE600]/50 hover:bg-white/10 transition-all duration-200 cursor-pointer group"
              onClick={() => setShowAuth(true)}
              data-testid={`featured-empty-${i}`}
            >
              <div className="h-36 flex items-center justify-center">
                <div className="text-center">
                  <Crown className="h-8 w-8 text-white/10 mx-auto mb-2 group-hover:text-[#FFE600]/40 transition-colors" />
                  <span className="text-sm font-bold text-white/10 group-hover:text-white/30 transition-colors">Il tuo spazio</span>
                </div>
              </div>
              <div className="p-4">
                <span className="text-[10px] font-bold uppercase tracking-widest text-white/15 group-hover:text-[#FFE600]/40 transition-colors">Disponibile</span>
                <p className="font-heading text-base text-white/15 group-hover:text-white/40 transition-colors">La tua attivita qui</p>
                <p className="text-[11px] text-white/10 mt-0.5">Solo 1 euro per 3 giorni</p>
              </div>
            </div>
          ))}
        </Marquee>
      </section>

      {/* FEATURES — Bento Grid */}
      <section className="py-20 px-6 lg:px-16 bg-[#FAFAFA]" data-testid="features-section">
        <motion.div className="max-w-7xl mx-auto" initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }} variants={stagger}>
          <motion.div variants={fadeUp} className="mb-12">
            <h2 className="font-heading text-4xl md:text-5xl tracking-tight text-[#09090B]">
              Perche <span className="text-[#FF4A00]">BookEasy</span>?
            </h2>
            <p className="text-lg text-[#3F3F46] mt-3 max-w-xl">Tutto quello che serve, niente che non serve.</p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { icon: Calendar, title: "Prenota Facile", desc: "Cerca, scegli orario, conferma. Fine.", color: "bg-[#00E5FF]" },
              { icon: CreditCard, title: "Pagamenti Sicuri", desc: "Stripe e Satispay. La tua cauzione e al sicuro.", color: "bg-[#FFE600]" },
              { icon: Globe, title: "5 Lingue", desc: "Italiano, Inglese, Spagnolo, Francese, Tedesco.", color: "bg-[#CCFF00]" },
              { icon: LayoutGrid, title: "44 Categorie", desc: "Medici, pizzerie, avvocati, spa e molto altro.", color: "bg-[#FF007F]" },
            ].map((f, i) => (
              <motion.div
                key={i}
                variants={fadeUp}
                className={`${f.color} border-2 border-[#09090B] rounded-2xl p-6 shadow-brutal hover:translate-x-[3px] hover:translate-y-[3px] hover:shadow-none transition-all duration-200 group`}
                data-testid={`feature-card-${i}`}
              >
                <div className="w-12 h-12 bg-white border-2 border-[#09090B] rounded-xl flex items-center justify-center mb-5 shadow-brutal-sm group-hover:rotate-6 transition-transform duration-300">
                  <f.icon className="h-6 w-6 text-[#09090B]" strokeWidth={2} />
                </div>
                <h3 className="font-heading text-xl text-[#09090B] mb-2">{f.title}</h3>
                <p className="text-sm text-[#09090B]/70 leading-relaxed">{f.desc}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-20 px-6 lg:px-16 bg-[#FF4A00] border-y-2 border-[#09090B]" data-testid="how-it-works-section">
        <motion.div className="max-w-7xl mx-auto" initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }} variants={stagger}>
          <motion.div variants={fadeUp} className="text-center mb-14">
            <h2 className="font-heading text-4xl md:text-5xl tracking-tight text-white">Come Funziona</h2>
            <p className="text-white/70 mt-3 text-lg">Tre passi. Trenta secondi. Fatto.</p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { step: "01", icon: Search, title: "Cerca", desc: "Trova attivita vicino a te per categoria o posizione.", color: "bg-[#FFE600]" },
              { step: "02", icon: Calendar, title: "Prenota", desc: "Scegli data e ora dal calendario condiviso.", color: "bg-[#00E5FF]" },
              { step: "03", icon: Shield, title: "Conferma", desc: "Paga la cauzione e ricevi la conferma istantanea.", color: "bg-[#CCFF00]" },
            ].map((s, i) => (
              <motion.div
                key={i}
                variants={fadeUp}
                className="bg-white border-2 border-[#09090B] rounded-2xl p-8 shadow-brutal hover:translate-x-[3px] hover:translate-y-[3px] hover:shadow-none transition-all duration-200"
                data-testid={`step-${i}`}
              >
                <div className={`${s.color} w-14 h-14 border-2 border-[#09090B] rounded-xl flex items-center justify-center mb-6 shadow-brutal-sm`}>
                  <s.icon className="h-7 w-7 text-[#09090B]" strokeWidth={2} />
                </div>
                <span className="text-xs font-bold uppercase tracking-widest text-[#09090B]/40">Passo {s.step}</span>
                <h3 className="font-heading text-2xl text-[#09090B] mt-1 mb-2">{s.title}</h3>
                <p className="text-[#3F3F46] leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* TWO PATHS — Split Screen */}
      <section className="py-20 px-6 lg:px-16 bg-[#FAFAFA]" data-testid="two-paths-section">
        <motion.div className="max-w-7xl mx-auto" initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }} variants={stagger}>
          <motion.div variants={fadeUp} className="text-center mb-14">
            <h2 className="font-heading text-4xl md:text-5xl tracking-tight text-[#09090B]">
              Due Modi per Usare BookEasy
            </h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Customer */}
            <motion.button
              variants={fadeUp}
              data-testid="path-customer"
              onClick={() => goAction("customer")}
              className="text-left bg-[#00E5FF] border-2 border-[#09090B] rounded-3xl p-8 lg:p-10 shadow-brutal-lg hover:translate-x-[5px] hover:translate-y-[5px] hover:shadow-none transition-all duration-200 group"
            >
              <div className="w-14 h-14 bg-white border-2 border-[#09090B] rounded-xl flex items-center justify-center mb-6 shadow-brutal-sm group-hover:rotate-6 transition-transform">
                <Calendar className="h-7 w-7 text-[#09090B]" strokeWidth={2} />
              </div>
              <h3 className="font-heading text-3xl text-[#09090B] mb-3">Per i Clienti</h3>
              <p className="text-[#09090B]/70 mb-6 leading-relaxed">Cerca attivita, prenota slot e paga in sicurezza.</p>
              <ul className="space-y-3 text-sm text-[#09090B]/80 mb-8">
                {["Sfoglia tutte le attivita", "Calendario in tempo reale", "Paga con Stripe o Satispay"].map((f, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <CheckCircle className="h-5 w-5 text-[#09090B] shrink-0" strokeWidth={2} />
                    {f}
                  </li>
                ))}
              </ul>
              <span className="btn-brutal inline-flex items-center bg-white text-[#09090B] text-sm">
                Prenota Ora <ArrowRight className="ml-2 h-4 w-4" />
              </span>
            </motion.button>

            {/* Business */}
            <motion.button
              variants={fadeUp}
              data-testid="path-business"
              onClick={() => goAction("business")}
              className="text-left bg-[#FFE600] border-2 border-[#09090B] rounded-3xl p-8 lg:p-10 shadow-brutal-lg hover:translate-x-[5px] hover:translate-y-[5px] hover:shadow-none transition-all duration-200 group"
            >
              <div className="w-14 h-14 bg-white border-2 border-[#09090B] rounded-xl flex items-center justify-center mb-6 shadow-brutal-sm group-hover:-rotate-6 transition-transform">
                <Briefcase className="h-7 w-7 text-[#09090B]" strokeWidth={2} />
              </div>
              <h3 className="font-heading text-3xl text-[#09090B] mb-3">Per le Attivita</h3>
              <p className="text-[#09090B]/70 mb-6 leading-relaxed">Gestisci prenotazioni, clienti e pagamenti.</p>
              <ul className="space-y-3 text-sm text-[#09090B]/80 mb-8">
                {["Dashboard completa", "Gestione menu e tavoli", "Pagamenti automatici"].map((f, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <CheckCircle className="h-5 w-5 text-[#09090B] shrink-0" strokeWidth={2} />
                    {f}
                  </li>
                ))}
              </ul>
              <span className="btn-brutal inline-flex items-center bg-white text-[#09090B] text-sm">
                Registra Attivita <ArrowRight className="ml-2 h-4 w-4" />
              </span>
            </motion.button>
          </div>
        </motion.div>
      </section>

      {/* QR CODE SECTION */}
      <section className="py-16 px-6 bg-[#FDFBF7]">
        <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="max-w-md mx-auto text-center">
          <h3 className="font-heading text-2xl text-[#09090B] mb-3">Scarica l'App</h3>
          <p className="text-[#6A564A] mb-6 text-sm">Scansiona il QR code per aprire BookEasy sul tuo dispositivo</p>
          <div className="inline-block p-4 bg-white border-2 border-[#09090B] rounded-2xl shadow-brutal">
            <img src="/qrcode-bookeasy.png" alt="QR Code BookEasy" className="w-48 h-48" data-testid="qr-code" />
          </div>
          <p className="mt-4 text-xs text-[#9CA3AF]">Funziona su iPhone, Android, Tablet e PC</p>
        </motion.div>
      </section>

      {/* FOOTER */}
      <footer className="py-10 px-6 bg-[#09090B] border-t-2 border-[#09090B]">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row justify-between items-center gap-4">
          <span className="font-heading text-xl text-white">
            Book<span className="text-[#FF4A00]">Easy</span>
          </span>
          <span className="text-sm text-white/30">2026 BookEasy — Prenota, paga, fatto.</span>
        </div>
      </footer>

      {/* Auth Modal */}
      <AuthModal isOpen={showAuth} onClose={() => setShowAuth(false)} onSuccess={() => window.location.reload()} />
    </div>
  );
}
